import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, MapPin, User, Calendar } from "lucide-react";

// Add animation styles
const styles = `
  @keyframes checkmarkPop {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  .checkmark-animated {
    animation: checkmarkPop 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
  }
`;

export default function PurchaseSuccess() {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);

    // Track Meta Pixel Purchase event
    if (window.fbq) {
      window.fbq('track', 'Purchase', {
        value: 19.99,
        currency: 'USD',
        content_name: 'Tour in a Box',
        content_type: 'product',
        content_ids: ['tour-purchase'],
      });
    }
  }, []);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-8"
      style={{
        background: 'linear-gradient(135deg, #FFFADF 0%, #FFE8C8 100%)',
        fontFamily: 'Urbanist, sans-serif'
      }}
    >
      {/* Inject animation styles */}
      <style>{styles}</style>

      {/* Logo */}
      <div className="flex justify-center mb-6">
        <img
          src="/images/flatlogo.png"
          alt="Tour in a Box"
          className="h-12 w-auto object-contain"
        />
      </div>

      {/* Success Card */}
      <div
        className="w-full max-w-lg rounded-3xl shadow-2xl p-8 text-center"
        style={{
          backgroundColor: 'white',
          border: '2px solid #E4B4A6'
        }}
      >
        {/* Success Icon */}
        <div className="flex justify-center mb-6">
          <div
            className="rounded-full p-4 checkmark-animated"
            style={{
              backgroundColor: 'rgba(56, 91, 55, 0.1)'
            }}
          >
            <CheckCircle2
              size={64}
              style={{ color: '#385B37' }}
              strokeWidth={2}
            />
          </div>
        </div>

        {/* Thank You Message */}
        <h1
          className="text-3xl font-bold mb-3"
          style={{
            color: '#413C04',
            fontFamily: 'Rubik, sans-serif'
          }}
        >
          Thank You for Your Purchase!
        </h1>

        <p
          className="text-lg mb-8"
          style={{ color: '#413C04', opacity: 0.8 }}
        >
          You're all set for your adventure!
        </p>

        {/* Info Cards */}
        <div className="space-y-4 mb-8 text-left">
          {/* Access Info */}
          <div
            className="rounded-xl p-4 flex items-start gap-3"
            style={{
              backgroundColor: 'rgba(143, 179, 211, 0.15)',
              border: '1px solid #8FB3D3'
            }}
          >
            <MapPin
              size={24}
              style={{ color: '#8FB3D3', flexShrink: 0 }}
              className="mt-0.5"
            />
            <div>
              <h3 className="font-semibold mb-1" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>
                Access Your Tour Anytime
              </h3>
              <p className="text-sm" style={{ color: '#413C04', opacity: 0.8 }}>
                Visit <span className="font-semibold">play.tourinabox.com</span> to start your adventure
              </p>
            </div>
          </div>

          {/* Account Setup */}
          <div
            className="rounded-xl p-4 flex items-start gap-3"
            style={{
              backgroundColor: 'rgba(248, 102, 43, 0.1)',
              border: '1px solid #F8662B'
            }}
          >
            <User
              size={24}
              style={{ color: '#F8662B', flexShrink: 0 }}
              className="mt-0.5"
            />
            <div>
              <h3 className="font-semibold mb-1" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>
                Create Your Account
              </h3>
              <p className="text-sm" style={{ color: '#413C04', opacity: 0.8 }}>
                Set up your username and password to get started
              </p>
            </div>
          </div>

          {/* Validity Period */}
          <div
            className="rounded-xl p-4 flex items-start gap-3"
            style={{
              backgroundColor: 'rgba(56, 91, 55, 0.1)',
              border: '1px solid #385B37'
            }}
          >
            <Calendar
              size={24}
              style={{ color: '#385B37', flexShrink: 0 }}
              className="mt-0.5"
            />
            <div>
              <h3 className="font-semibold mb-1" style={{ color: '#413C04', fontFamily: 'Rubik, sans-serif' }}>
                Valid for 1 Year
              </h3>
              <p className="text-sm" style={{ color: '#413C04', opacity: 0.8 }}>
                Your tour access is valid for one full year from today
              </p>
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div>
          <button
            onClick={() => navigate('/signup')}
            className="w-full py-3.5 rounded-full font-semibold text-base shadow-lg hover:opacity-90 transition-opacity"
            style={{
              backgroundColor: '#F8662B',
              color: 'white'
            }}
          >
            Create Account
          </button>
        </div>
      </div>

      {/* Footer Note */}
      <p
        className="mt-6 text-sm text-center max-w-md"
        style={{ color: '#413C04', opacity: 0.6 }}
      >
        Questions? Contact us at{" "}
        <a
          href="mailto:hello@tourinabox.com"
          className="underline hover:opacity-70"
          style={{ color: '#F8662B' }}
        >
          hello@tourinabox.com
        </a>
      </p>
    </div>
  );
}
